import { ChangeEvent, useState } from "react";
import { Checkbox } from "@mui/material";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import {
  DialogFrame,
  useDialog,
} from "../../../components/Dialogs/dialog-base";
import { CustomInput } from "../../../components/Input/CustomInput";
import { useOrderActionHandler } from "../hooks/useOrderActionsHandler";
import { CartItemDesign } from "../models/CartItem";
import { DesignDetails } from "./DesignDetails";
import { LogoTextEditor } from "./LogoTextEditor";
import { OrderLineViewDesignPrint } from "./OrderLineViewDesignPrint";
import { CasingOption } from "../../../shared/functions/casing";

interface OrderLineViewDesignProps {
  orderline: CartItemDesign;
}

export function OrderLineViewDesign({ orderline }: OrderLineViewDesignProps) {
  const dialogOptions = useDialog();
  const [customerFillsData, setCustomerFillsData] = useState(
    orderline.customerEntersNamesAndNumbers ?? false
  );
  const [customerEmail, setCustomerEmail] = useState(
    orderline.customerEntersNamesAndNumbersEmail ?? ""
  );
  const { handleDesignInstanceUpdateStorage } = useOrderActionHandler();
  const [casing, setCasing] = useState(
    orderline.casing ?? CasingOption.UserDefined
  );

  const designLogos = orderline.design.designLogos;
  const canCustomerFillData = !!designLogos.find((l) =>
    ["NameLogo", "NumberLogo"].includes(l.logo.logoType)
  );

  const toggleCustomerFillsData = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCustomerFillsData(checked);
    if (checked) dialogOptions.openDialog();
    else setCustomerEmail("");
    handleDesignInstanceUpdateStorage({
      ...orderline,
      customerEntersNamesAndNumbersEmail: "",
      customerEntersNamesAndNumbers: false,
    });
  };

  const updateCustomerFillsData = () => {
    // NOTE: Beautiful email regex:
    if (/.+@.+\..+/.test(customerEmail)) {
      dialogOptions.closeDialog();
      handleDesignInstanceUpdateStorage({
        ...orderline,
        customerEntersNamesAndNumbersEmail: customerEmail,
        customerEntersNamesAndNumbers: customerFillsData,
      });
    }
  };

  const updateCasing = (casing: CasingOption) => {
    setCasing(casing);
    handleDesignInstanceUpdateStorage({ ...orderline, casing });
  };

  return (
    <div id="main-box" className="flex-1 flex space-x-10">
      <div id="left-box" className="space-y-2 flex-1">
        <div className="flex-1">
          <DesignDetails orderline={orderline} />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase mb-2">PRINTS</p>
          <div className="overflow-x-hidden overflow-y-scroll pr-1 h-[370px] grid grid-cols-2 gap-x-3 gap-y-4">
            {designLogos.map((designLogo) => (
              <OrderLineViewDesignPrint
                key={designLogo.id}
                designLogo={designLogo}
                orderline={orderline}
              />
            ))}
          </div>
        </div>
      </div>
      <div id="right-box" className="flex-1 flex flex-col mb-8">
        <div className={`${customerFillsData ? 'opacity-50 pointer-events-none' : ''}`}>
          <LogoTextEditor
            orderlineDesign={orderline}
            casing={casing}
            setCasing={updateCasing}
          />
        </div>
        {canCustomerFillData && (
          <div className="flex flex-row items-center justify-end pt-2">
            <Checkbox
              checked={customerFillsData}
              onChange={toggleCustomerFillsData}
            />
            CUSTOMER WRITES NAMES AND NUMBERS
          </div>
        )}
      </div>

      <DialogFrame dialogOption={dialogOptions}>
        <div className="flex flex-col space-y-4 p-4">
          <p className="text-left text-base font-bold">
            Design (Names and Numbers)
          </p>
          <p className="text-left">
            Type the Email you with to fill out names and numbers
          </p>
          <div className="flex flex-col justify-center space-y-4">
            <CustomInput
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
            <div className="flex flex-row justify-between space-x-10">
              <SecondaryButton onClick={dialogOptions.closeDialog}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                className="font-bold"
                onClick={updateCustomerFillsData}
              >
                Send
              </PrimaryButton>
            </div>
          </div>
        </div>
      </DialogFrame>
    </div>
  );
}
